import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import ClubLifePostCard from "../common/ClubLifePostCard";
import ClubLifeComposer from "./ClubLifeComposer";
import ClientChildSwitcher from "./ClientChildSwitcher";
import ClientPostManageModals from "./ClientPostManageModals";
import { useClubLifeInteractions } from "../../../hooks/useClubLifeInteractions";
import { useClientPostManage } from "../../../hooks/useClientPostManage";
import { useClientRefresh } from "../../../hooks/useClientRefresh";
import useClubPortalBranding from "../../../hooks/useClubPortalBranding";
import { useClientAthlete } from "../../../context/ClientAthleteContext";
import {
  mediaUrl,
  TRAINER_COLOR_PRESETS,
} from "../../../utils/clientHeroTheme";
import { personDisplayName, trainerPersonalChatTitle } from "../../../utils/clientAppUtils";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import ClientCompetitionMedals from "./ClientCompetitionMedals";
import { Avatar, Button, Skeleton, useToast } from "../../ui";
import s from "./clientApp.module.css";
import md from "../common/manageDesktop.module.css";

function themeStyle(themeId) {
  const preset = TRAINER_COLOR_PRESETS.find((p) => p.id === themeId);
  if (!preset) return {};
  return {
    "--member-accent": preset.color,
    "--ca-accent": preset.color,
  };
}

function formatRank(card) {
  if (!card?.rank_place) return "—";
  if (card.rank_total) return `${card.rank_place} из ${card.rank_total}`;
  return `${card.rank_place} место`;
}

function parentRowMeta(parent, myId, isParentMode) {
  if (isParentMode && String(parent.id) === String(myId)) {
    return "Ваш аккаунт";
  }
  return [parent.relation, parent.phone].filter(Boolean).join(" · ") || "Родитель";
}

export default function ClientMemberProfile({ profileTab = false }) {
  const { userId: paramUserId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isMobile = useIsMobile(768);
  const desktop = !isMobile;
  const myId = useSelector((state) => state.auth.profile?.id);
  const authProfile = useSelector((state) => state.auth.profile);
  const { activeAthleteId, activeAthlete, isParentMode, athletes, setActiveAthleteId } = useClientAthlete();
  const userId = profileTab
    ? isParentMode && activeAthleteId
      ? activeAthleteId
      : myId
    : paramUserId;
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState("wall");
  const [wallPosts, setWallPosts] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [competitionMedals, setCompetitionMedals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(true);
  const [followBusy, setFollowBusy] = useState(false);
  const coverInputRef = useRef(null);
  const { heroStyle } = useClubPortalBranding("ca");

  const isSelf = Boolean(profile?.is_self) || String(userId) === String(myId);
  // Родитель смотрит карточку ребёнка из семьи — это «свой» профиль, не чужой участник
  const isFamilyManaged =
    Boolean(profile?.is_family_managed) ||
    (isParentMode &&
      Boolean(userId) &&
      athletes.some((a) => String(a.id) === String(userId)));
  const isStaffProfile = ["owner", "admin", "trainer"].includes(profile?.role);
  const clubCard = profile?.club_card;
  const familyChildren = athletes.filter(
    (a) => !a.is_self && String(a.id) !== String(myId),
  );
  const viewingFamilyChild =
    profileTab && isParentMode && activeAthlete && !activeAthlete.is_self;
  const isOwnProfile = isSelf || isFamilyManaged || viewingFamilyChild;

  const familyParents = useMemo(() => {
    return (profile?.family_guardians || []).map((g) => ({
      id: g.id,
      full_name: g.full_name,
      photo_url: g.photo_url,
      relation: g.relation,
      phone: g.phone,
    }));
  }, [profile?.family_guardians]);

  const showParentBlock =
    familyParents.length > 0 &&
    !(isOwnProfile && isParentMode && activeAthlete?.is_self);

  const {
    commentBusy,
    openComments,
    commentsByPost,
    toggleLike,
    loadComments,
    addComment,
  } = useClubLifeInteractions(tab === "wall" ? setWallPosts : setAchievements);
  const postManage = useClientPostManage(tab === "wall" ? setWallPosts : setAchievements);

  const loadProfile = useCallback(async () => {
    if (!userId) return;
    try {
      const data = await clinicAPI.getSocialMemberProfile(userId);
      setProfile(data.profile || null);
      setCompetitionMedals(data.competition_medals || []);
    } catch (err) {
      setProfile(null);
      const status = err?.response?.status;
      const msg = err?.response?.data?.error;
      if (status === 403) {
        toast.error("Профиль закрыт");
      } else if (status === 404) {
        toast.error(msg || "Участник не найден");
      } else if (msg) {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [userId, toast]);

  const loadPosts = useCallback(async () => {
    if (!userId) return;
    setPostsLoading(true);
    try {
      if (tab === "wall") {
        const data = await clinicAPI.getSocialMemberWall(userId);
        setWallPosts(data.posts || []);
      } else {
        const data = await clinicAPI.getSocialMemberAchievements(userId);
        setAchievements(data.posts || []);
      }
    } catch {
      if (tab === "wall") setWallPosts([]);
      else setAchievements([]);
    } finally {
      setPostsLoading(false);
    }
  }, [tab, userId]);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    loadProfile();
  }, [loadProfile, userId]);

  useEffect(() => {
    if (!userId || !profile) return;
    loadPosts();
  }, [profile, loadPosts, userId]);

  useClientRefresh(() => {
    loadProfile();
    loadPosts();
  });

  const posts = tab === "wall" ? wallPosts : achievements;
  const shellStyle = useMemo(
    () => themeStyle(profile?.profile_theme),
    [profile?.profile_theme],
  );

  const primaryGroup =
    clubCard?.primary_group ||
    profile?.groups?.[0]?.name ||
    null;

  const toggleFollow = async () => {
    if (!profile || isOwnProfile) return;
    setFollowBusy(true);
    try {
      const fn = profile.is_following
        ? clinicAPI.unfollowSocialMember
        : clinicAPI.followSocialMember;
      const r = await fn(userId);
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              is_following: r.is_following,
              followers_count: r.followers_count,
            }
          : prev,
      );
    } catch {
      toast.error("Не удалось обновить подписку");
    } finally {
      setFollowBusy(false);
    }
  };

  const openFamilyParent = (parent) => {
    if (!parent?.id) return;
    if (isParentMode) {
      setActiveAthleteId(parent.id);
      if (!profileTab) navigate("/app/profile");
      return;
    }
    navigate(`/app/members/${parent.id}`);
  };

  const writeToMember = () => {
    if (isOwnProfile) return;
    const draftName = (profile?.full_name || "").trim().split(/\s+/)[0];
    const draft = draftName ? `${draftName}, ` : "";
    const chatTitle = isStaffProfile
      ? trainerPersonalChatTitle(profile?.full_name) || profile?.full_name || "Тренер"
      : profile?.full_name || "Участник";
    navigate(`/app/chat/member/${userId}`, {
      state: {
        draft,
        name: chatTitle,
      },
    });
  };

  const handleWallPublished = useCallback(() => {
    loadPosts();
    loadProfile();
  }, [loadPosts, loadProfile]);

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await clinicAPI.uploadSocialCover(file);
      setProfile((prev) =>
        prev ? { ...prev, profile_cover_url: r.url } : prev,
      );
    } catch {
      toast.error("Не удалось загрузить обложку");
    }
    e.target.value = "";
  };

  if (!userId || loading) {
    return <Skeleton height={320} style={{ borderRadius: 16 }} />;
  }

  if (!profile) {
    return (
      <div className={s.socialEmpty}>
        <span>🔒</span>
        <p>Профиль недоступен</p>
        <Button type="button" onClick={() => navigate(-1)}>
          Назад
        </Button>
      </div>
    );
  }

  const coverUrl = mediaUrl(profile.profile_cover_url);
  const avatarPhoto = mediaUrl(
    isOwnProfile
      ? profile.photo_url ||
          (isSelf ? authProfile?.photo_url : null) ||
          activeAthlete?.photo_url
      : profile.photo_url,
  );

  const statsLine = (
    <p className={s.memberVkSubline}>
      <strong>{profile.wall_posts_count ?? 0}</strong> записей ·{" "}
      <strong>{profile.followers_count ?? 0}</strong> подписчиков ·{" "}
      <strong>{profile.following_count ?? 0}</strong> подписок
    </p>
  );

  const profileActions = isOwnProfile ? (
    <>
      <Link
        to="/app/profile/account/social"
        className={s.memberBtnOutline}
      >
        Редактировать
      </Link>
      <Link to="/app/profile/account" className={s.memberBtnOutline}>
        Настройки
      </Link>
    </>
  ) : (
    <>
      <button
        type="button"
        className={
          profile?.is_following ? s.memberBtnOutline : s.memberBtnPrimary
        }
        onClick={toggleFollow}
        disabled={followBusy}
      >
        {followBusy
          ? "…"
          : profile?.is_following
            ? "Вы подписаны"
            : "Подписаться"}
      </button>
      <button
        type="button"
        className={s.memberBtnOutline}
        onClick={writeToMember}
      >
        Написать
      </button>
    </>
  );

  return (
    <div
      className={`${s.memberProfilePage} ${desktop ? md.desktopOwnerMemberPage : ""}`}
      style={shellStyle}
    >
      <header
        className={`${s.memberVkHeader} ${desktop ? md.desktopMemberVkHeader : ""}`}
      >
        <div className={s.memberCoverWrap}>
          <div className={s.memberCover}>
            <div
              className={s.memberCoverBg}
              style={
                coverUrl
                  ? { backgroundImage: `url(${coverUrl})` }
                  : heroStyle
              }
            />
            {!profileTab ? (
              <button
                type="button"
                className={s.memberBackBtn}
                onClick={() => navigate(-1)}
                aria-label="Назад"
              >
                ←
              </button>
            ) : null}
            {desktop && isSelf ? (
              <div className={md.desktopCoverToolbar}>
                <button
                  type="button"
                  className={`${s.memberCoverEdit} ${md.desktopCoverUploadBtn}`}
                  onClick={() => coverInputRef.current?.click()}
                >
                  {coverUrl ? "Заменить обложку" : "Загрузить обложку"}
                </button>
              </div>
            ) : (
              <>
                {isSelf ? (
                  <button
                    type="button"
                    className={
                      profileTab
                        ? s.memberCoverEdit
                        : `${s.memberCoverEdit} ${s.memberCoverEditAlt}`
                    }
                    onClick={() => coverInputRef.current?.click()}
                  >
                    Обложка
                  </button>
                ) : null}
                {profileTab ? (
                  <Link
                    to="/app/profile/account"
                    className={s.memberSettingsBtn}
                    aria-label="Настройки"
                  >
                    ⚙
                  </Link>
                ) : null}
              </>
            )}
            {isSelf ? (
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={uploadCover}
              />
            ) : null}
          </div>
        </div>

        <div
          className={`${s.memberVkProfile} ${desktop ? md.desktopMemberVkProfile : ""}`}
        >
          <div className={s.memberVkTopRow}>
            <div className={s.memberVkAvatarWrap}>
              <Avatar
                name={personDisplayName(profile.full_name || authProfile?.full_name) || "?"}
                src={avatarPhoto}
                size="lg"
                className={s.memberVkAvatar}
              />
            </div>
            <div className={s.memberVkTitleBlock}>
              <h1 className={s.memberVkName}>
                {personDisplayName(profile.full_name) || "Участник"}
              </h1>
              {isStaffProfile ? (
                <p className={s.memberFamilyContext}>
                  {profile.role === "owner"
                    ? "Владелец клуба"
                    : profile.role === "admin"
                      ? "Администратор"
                      : "Тренер"}
                </p>
              ) : null}
              {viewingFamilyChild ? (
                <p className={s.memberFamilyContext}>
                  Профиль спортсмена
                </p>
              ) : null}
              {statsLine}
              {profile.profile_bio ? (
                <p className={s.memberVkBio}>{profile.profile_bio}</p>
              ) : null}
            </div>
            {desktop ? (
              <div className={`${s.memberVkHeadActions} ${md.desktopVkHeadActions}`}>
                {profileActions}
              </div>
            ) : null}
          </div>

          {profileTab && isParentMode && athletes.length > 1 ? (
            <div className={s.memberProfileSwitcher}>
              <ClientChildSwitcher variant="profile" />
            </div>
          ) : null}

          {!isStaffProfile ? (
          <div className={s.memberClubStrip}>
          <div className={s.memberClubTile}>
            <span className={s.memberClubTileLabel}>Группа</span>
            <strong className={s.memberClubTileValue}>
              {primaryGroup || "—"}
            </strong>
          </div>
          {isOwnProfile ? (
            <Link to="/app/stats" className={s.memberClubTile}>
              <span className={s.memberClubTileLabel}>Рейтинг</span>
              <strong className={s.memberClubTileValue}>
                {formatRank(clubCard)}
              </strong>
              {clubCard?.attendance_pct != null ? (
                <small className={s.memberClubTileHint}>
                  {clubCard.attendance_pct}% явка
                </small>
              ) : null}
            </Link>
          ) : (
            <div className={s.memberClubTile}>
              <span className={s.memberClubTileLabel}>Рейтинг</span>
              <strong className={s.memberClubTileValue}>
                {formatRank(clubCard)}
              </strong>
              {clubCard?.attendance_pct != null ? (
                <small className={s.memberClubTileHint}>
                  {clubCard.attendance_pct}% явка
                </small>
              ) : null}
            </div>
          )}
          {isOwnProfile ? (
            <Link to="/app/profile/account/club" className={s.memberClubTile}>
              <span className={s.memberClubTileLabel}>Абонемент</span>
              <strong className={s.memberClubTileValue}>
                {clubCard?.membership_label || activeAthlete?.membership_label || "—"}
              </strong>
            </Link>
          ) : (
            <div className={s.memberClubTile}>
              <span className={s.memberClubTileLabel}>Медали</span>
              <strong className={s.memberClubTileValue}>
                {profile.competition_medals_count ?? competitionMedals.length ?? 0}
              </strong>
            </div>
          )}
          {isSelf &&
          isParentMode &&
          !viewingFamilyChild &&
          familyChildren.length > 0 ? (
            <Link to="/app/profile/account/kids" className={s.memberClubTile}>
              <span className={s.memberClubTileLabel}>Дети</span>
              <strong className={s.memberClubTileValue}>{familyChildren.length}</strong>
              <small className={s.memberClubTileHint}>в семье</small>
            </Link>
          ) : null}
        </div>
          ) : null}

        {!isStaffProfile ? (
          <ClientCompetitionMedals medals={competitionMedals} />
        ) : null}
        <div className={s.memberBtnRow}>
          {!desktop ? profileActions : null}
        </div>

        {showParentBlock ? (
          <div
            className={`${s.memberFamilyChildren} ${s.memberFamilyParents} ${s.memberFamilyChildrenAfterActions}`}
          >
            <div className={s.memberFamilyChildrenHead}>
              <span>
                {familyParents.length === 1 ? "Родитель" : "Родители"}
              </span>
            </div>
            <div className={s.memberFamilyChildrenList}>
              {familyParents.map((parent) => (
                <button
                  key={parent.id || parent.full_name}
                  type="button"
                  className={s.memberFamilyChildBtn}
                  onClick={() => openFamilyParent(parent)}
                >
                  <Avatar
                    name={personDisplayName(parent.full_name) || "?"}
                    src={mediaUrl(parent.photo_url)}
                    size="sm"
                  />
                  <span>
                    <strong>
                      {isParentMode && String(parent.id) === String(myId)
                        ? "Я"
                        : personDisplayName(parent.full_name)}
                    </strong>
                    <em>{parentRowMeta(parent, myId, isParentMode)}</em>
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {isParentMode && !viewingFamilyChild && familyChildren.length > 0 ? (
          <div className={`${s.memberFamilyChildren} ${s.memberFamilyChildrenAfterActions}`}>
            <div className={s.memberFamilyChildrenHead}>
              <span>Дети в клубе</span>
              <Link to="/app/profile/account/kids">Все</Link>
            </div>
            <div className={s.memberFamilyChildrenList}>
              {familyChildren.map((child) => {
                const active = String(child.id) === String(activeAthleteId);
                return (
                <button
                  key={child.id}
                  type="button"
                  className={
                    active
                      ? `${s.memberFamilyChildBtn} ${s.memberFamilyChildBtnOn}`
                      : s.memberFamilyChildBtn
                  }
                  onClick={() => setActiveAthleteId(child.id)}
                >
                  <Avatar name={personDisplayName(child.full_name) || "?"} size="sm" />
                  <span>
                    <strong>{personDisplayName(child.full_name)}</strong>
                    <em>{child.membership_label || "Без абонемента"}</em>
                  </span>
                </button>
                );
              })}
            </div>
          </div>
        ) : null}
        </div>
      </header>

      <div className={`${s.memberBody} ${desktop ? md.desktopMemberBody : ""}`}>
        <nav className={s.memberTabs} aria-label="Разделы профиля">
          <button
            type="button"
            className={tab === "wall" ? s.memberTabOn : s.memberTab}
            onClick={() => setTab("wall")}
          >
            Стена
          </button>
          <button
            type="button"
            className={tab === "achievements" ? s.memberTabOn : s.memberTab}
            onClick={() => setTab("achievements")}
          >
            Достижения
          </button>
        </nav>

        {isOwnProfile && tab === "wall" ? (
          <div className={s.memberComposerSlot}>
            <ClubLifeComposer
              compact
              embedded
              wallMode
              wallAthleteId={userId}
              onPublished={handleWallPublished}
            />
          </div>
        ) : null}

        {postsLoading ? (
          <Skeleton
            height={160}
            style={{ borderRadius: 16, margin: "12px 0" }}
          />
        ) : posts.length === 0 ? (
          <div className={s.memberFeedEmpty}>
            <span>{tab === "wall" ? "📝" : "🏆"}</span>
            <p>
              {tab === "wall"
                ? isOwnProfile
                  ? "Пока нет записей. Напишите на стене или поделитесь в ленте клуба на главной."
                  : "На стене пока нет записей"
                : "Достижений пока нет"}
            </p>
          </div>
        ) : (
          <div className={s.memberPosts}>
            {posts.map((post) => (
              <ClubLifePostCard
                key={post.id}
                post={post}
                mode="client"
                currentUserId={isOwnProfile ? userId : null}
                onOpenMenu={isOwnProfile ? postManage.openMenu : undefined}
                onLike={toggleLike}
                onLoadComments={loadComments}
                commentsOpen={openComments === post.id}
                comments={commentsByPost[post.id] || []}
                onComment={addComment}
                commentBusy={commentBusy}
              />
            ))}
          </div>
        )}
      </div>
      {isOwnProfile ? <ClientPostManageModals {...postManage} /> : null}
    </div>
  );
}
